import React, { useState } from 'react';
import { X, MapPin, User, Calendar, Phone } from 'lucide-react';
import { darkModeClasses } from '../../utils/darkMode';
import { HelpButton } from '../Help/HelpButton';

interface AddTownModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (townData: any) => void;
}

export const AddTownModal: React.FC<AddTownModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    contactPerson: '',
    contactPhone: '',
    setupDate: new Date().toISOString().split('T')[0]
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Town name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }
    
    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Contact phone is required';
    } else if (!/^(\+233|0)[2-9]\d{8}$/.test(formData.contactPhone)) {
      newErrors.contactPhone = 'Please enter a valid Ghana phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const townData = {
      ...formData,
      id: Date.now().toString(),
      memberCount: 0,
      totalRevenue: 0,
      totalExpenses: 0,
      currentBalance: 0
    };
    
    onSave(townData);
    onClose();
    setFormData({
      name: '',
      location: '',
      contactPerson: '',
      contactPhone: '',
      setupDate: new Date().toISOString().split('T')[0]
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto ${darkModeClasses.container}`}>
        <div className={`flex items-center justify-between p-6 border-b ${darkModeClasses.border}`}>
          <h2 className={`text-xl font-semibold flex items-center ${darkModeClasses.textPrimary}`}>
            <MapPin className="h-6 w-6 mr-2 text-blue-600" />
            Add New Town
          </h2>
          <div className="flex items-center space-x-2">
            <HelpButton guideId="towns/add-town" variant="icon" size="sm" />
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Town Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className={`w-full rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-500' : darkModeClasses.input
              }`}
              placeholder="e.g., ACCRA, KUMASI"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Location *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.location ? 'border-red-500' : darkModeClasses.input
                }`}
                placeholder="e.g., Greater Accra Region"
              />
            </div>
            {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Contact Person *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.contactPerson}
                onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.contactPerson ? 'border-red-500' : darkModeClasses.input
                }`}
                placeholder="Full name of contact person"
              />
            </div>
            {errors.contactPerson && <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Contact Phone *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.contactPhone ? 'border-red-500' : darkModeClasses.input
                }`}
                placeholder="+233 24 123 4567"
              />
            </div>
            {errors.contactPhone && <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkModeClasses.textSecondary}`}>
              Setup Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={formData.setupDate}
                onChange={(e) => setFormData({...formData, setupDate: e.target.value})}
                className={`w-full rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkModeClasses.input}`}
              />
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <MapPin className="h-4 w-4" />
              <span>Add Town</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};